import React, { useCallback, useEffect } from 'react';
import BaseDataGrid from 'common/components/base-data-grid';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { Box, Checkbox, Typography } from '@mui/material';
import { createStyleSheet } from 'utils/helpers';
import { useAppSelector } from 'common/hook';
import { RootState } from 'plugins/store';
import { toast } from 'react-toastify';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { handleError } from 'utils/helpers';
import CustomPagination from 'pages/admin/campaign/components/custom-pagination';
import CampaignCheckBoxChecked from '../../campaign/components/icons/campaign-check-box-checked';
import CampaignCheckBoxUnchecked from '../../campaign/components/icons/campaign-check-box-uncheck';
import UniqLinkGridColumn from './uniq-link-grid-column';
import {
    IUniqLink,
    IUniqLinkSearchForm,
    IListUniqLinksPayload,
    IUniqLinkResponse,
} from 'types/uniq-link';
import ModalConfirm from 'common/components/modal-confirm';
import { listUniqLinks, deleteUniqLink } from 'services/uniqLink.service';

interface UniqLinkDataGridProps {
    onOpenForm: (link: IUniqLink | null) => void;
}

const UniqLinkDataGrid: React.FC<UniqLinkDataGridProps> = ({ onOpenForm }) => {
    const [uniqLinks, setUniqLinks] = React.useState<IUniqLink[]>([]);
    const uniqLinksSearchForm = useAppSelector(
        (state: RootState) => state.uniqLink?.uniqLinksSearchForm || {}
    );
    const [openModalDeleteLink, setOpenModalDeleteLink] = React.useState(false);
    const [linkSelected, setLinkSelected] = React.useState<IUniqLink | null>();
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 10,
    });
    const [rowCount, setRowCount] = React.useState(0);

    const transformParams = (
        formState: IUniqLinkSearchForm,
        page: number
    ): IListUniqLinksPayload => {
        return {
            page: page,
            q: {
                search_with_key_cont: formState.searchKeyWord,
                start_date: formState.startDate ?? null,
                end_date: formState.endDate ?? null,
            },
        };
    };

    const fetchData = useCallback(
        async (page = 1) => {
            try {
                const response = await listUniqLinks(
                    transformParams(uniqLinksSearchForm, page)
                );
                if (!response.success) {
                    toast.error(response.message);
                    return;
                }
                const data = response.data.uniq_links || [];
                setUniqLinks(
                    data.map((item: IUniqLinkResponse) => {
                        const { attributes } = item;
                        return {
                            id: attributes.id,
                            customer_id: attributes.customer_id || '',
                            staff_id: attributes.staff_id || 0,
                            email: attributes.email,
                            phone: attributes.phone || '',
                            code: attributes.code || '',
                            url_survey: attributes.url_survey || '',
                            is_answered: attributes.is_answered || false,
                            survey_id: attributes.survey_id || 0,
                            answered_at: attributes.answered_at || null,
                            created_at: attributes.created_at,
                            updated_at: attributes.updated_at,
                        };
                    })
                );
                setRowCount(response.data.total_items || 0);
            } catch (error) {
                handleError(error);
            }
        },
        [uniqLinksSearchForm]
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onClickDeleteRow = (row: IUniqLink) => {
        setLinkSelected(row);
        setOpenModalDeleteLink(true);
    };

    const onClickEditRow = (row: IUniqLink) => {
        onOpenForm(row);
    };

    const handleConfirmDeleteLink = async () => {
        if (!linkSelected) {
            return;
        }
        const response = await deleteUniqLink(linkSelected.id);
        if (!response.success) {
            toast.error(response.message);
            return;
        }
        toast.success(response.message);
        setOpenModalDeleteLink(false);
        setUniqLinks(uniqLinks.filter((item) => item.id !== linkSelected.id));
    };

    const columns: GridColDef[] = UniqLinkGridColumn(
        onClickDeleteRow,
        onClickEditRow
    );

    function labelDisplayedRows({
        from,
        to,
        count,
    }: {
        from: number;
        to: number;
        count: number;
    }) {
        return `Xem ${from} - ${to} trên ${count !== -1 ? count : `hơn ${to}`}`;
    }

    const onCellClick = (cell: GridCellParams) => {
        if (cell.field === 'actions' || cell.field === '__check__') return;
        onClickEditRow(cell.row as IUniqLink);
    };

    return (
        <Box component="div">
            <BaseDataGrid
                onCellClick={(cell) => onCellClick(cell)}
                columns={columns}
                rows={uniqLinks}
                checkboxSelection
                disableRowSelectionOnClick
                disableColumnMenu
                autoHeight
                sx={styles.dataGrid}
                rowHeight={83}
                localeText={{
                    MuiTablePagination: {
                        labelDisplayedRows,
                    },
                    footerRowSelected: (count) => (
                        <Typography component="span" fontSize={'16px'}>
                            {count} hàng đã chọn
                        </Typography>
                    ),
                }}
                slots={{
                    pagination: CustomPagination,
                    noRowsOverlay: () => (
                        <Box
                            component="div"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height={'100%'}
                        >
                            <Typography component="span" fontSize={'16px'}>
                                Không có dữ liệu
                            </Typography>
                        </Box>
                    ),
                    baseCheckbox: (props) => (
                        <Checkbox
                            {...props}
                            checkedIcon={
                                <CampaignCheckBoxChecked
                                    sx={{
                                        color: (theme) =>
                                            theme.palette.text.primary,
                                    }}
                                />
                            }
                            icon={<CampaignCheckBoxUnchecked />}
                            indeterminateIcon={
                                <IndeterminateCheckBoxIcon
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                    }}
                                />
                            }
                        />
                    ),
                }}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[]}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) => {
                    setPaginationModel(newModel);
                    fetchData(newModel.page + 1);
                }}
                rowCount={rowCount}
            />
            {openModalDeleteLink && (
                <ModalConfirm
                    open={openModalDeleteLink}
                    onClose={() => setOpenModalDeleteLink(false)}
                    title={'Xác nhận xóa Unique Link'}
                    textOk="Xóa"
                    onOk={handleConfirmDeleteLink}
                    content={
                        <Box>
                            Bạn có muốn chắc chắn xóa Unique Link cho email{' '}
                            <b>{`" ${linkSelected?.email}"`}</b> này không?
                        </Box>
                    }
                />
            )}
        </Box>
    );
};

const styles = createStyleSheet({
    dataGrid: {
        '--DataGrid-rowBorderColor': 'transparent',
        '.MuiDataGrid-columnHeaders': {
            height: '83px',
            justifyContent: 'center',
        },
        '.MuiDataGrid-columnHeader': {
            padding: 0,
        },
        '.MuiDataGrid-columnHeaderTitle': {
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 700,
        },
        '.MuiDataGrid-columnHeaderTitleContainer': {
            display: 'flex',
        },
        '.MuiDataGrid-cell': {
            outline: 'none !important',
            padding: '0',
        },
        '.MuiDataGrid-footerContainer': {
            borderColor: 'transparent',
            marginBottom: '44px',
            marginRight: '54px',
        },
        '.MuiTablePagination-displayedRows': {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '17px',
        },
        border: 'none',
    },
});

export default UniqLinkDataGrid;
